import { API_BASE } from '@/shared/services/api';
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/shared/contexts/AuthContext';
import { toast } from 'sonner';
import { cn } from '@/shared/utils';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { Label } from '@/shared/components/ui/label';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import {
  Settings, HardDrive, Cloud, Database, Key, CheckCircle,
  AlertTriangle, RefreshCw, TestTube, ExternalLink, Info, 
  Server, Shield, Zap, Navigation
} from 'lucide-react';

const API_URL = API_BASE;

// Storage provider configurations
const STORAGE_PROVIDERS = {
  local: { 
    name: 'Local Filesystem', 
    icon: HardDrive, 
    color: 'text-slate-500',
    description: 'Store files on the server\'s local disk',
    pros: ['No external dependencies', 'Fast access', 'No API costs'],
    cons: ['Limited scalability', 'Single point of failure']
  },
  s3: { 
    name: 'Amazon S3', 
    icon: Cloud, 
    color: 'text-orange-500',
    description: 'Store files in an AWS S3 bucket',
    pros: ['Highly scalable', 'Durable', 'CDN integration'],
    cons: ['Requires AWS account', 'API costs']
  },
  gcs: { 
    name: 'Google Cloud Storage', 
    icon: Database, 
    color: 'text-blue-500',
    description: 'Store files in a GCS bucket',
    pros: ['Highly scalable', 'GCP integration', 'Multi-region'],
    cons: ['Requires GCP account', 'API costs']
  }
};

// Provider Card Component
const StorageProviderCard = ({ providerId, provider, settings, isActive, onSelect, onTest }) => {
  const ProviderIcon = provider.icon;
  const providerSettings = settings?.providers?.find(p => p.id === providerId);
  const isConfigured = providerSettings?.configured;

  return (
    <Card className={cn(
      'cursor-pointer transition-all hover:shadow-md',
      isActive && 'ring-2 ring-primary'
    )}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={cn('w-12 h-12 rounded-lg flex items-center justify-center bg-muted', provider.color)}>
              <ProviderIcon className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-semibold">{provider.name}</h3>
              <p className="text-sm text-muted-foreground">{provider.description}</p>
              <div className="flex items-center gap-2 mt-2">
                {isConfigured ? (
                  <Badge variant="outline" className="text-emerald-500 text-xs">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Configured
                  </Badge>
                ) : providerId !== 'local' ? (
                  <Badge variant="outline" className="text-amber-500 text-xs">
                    <Key className="h-3 w-3 mr-1" />
                    Keys Required
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-emerald-500 text-xs">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Ready
                  </Badge>
                )}
                {isActive && (
                  <Badge className="text-xs bg-primary">Active</Badge>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Pros/Cons */}
        <div className="grid grid-cols-2 gap-4 mt-4 text-xs">
          <div>
            <p className="font-medium text-emerald-500 mb-1">Pros:</p>
            <ul className="space-y-1 text-muted-foreground">
              {provider.pros.map((pro, i) => (
                <li key={i} className="flex items-center gap-1">
                  <CheckCircle className="h-3 w-3 text-emerald-500" />
                  {pro}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="font-medium text-amber-500 mb-1">Cons:</p>
            <ul className="space-y-1 text-muted-foreground">
              {provider.cons.map((con, i) => (
                <li key={i} className="flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3 text-amber-500" />
                  {con}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Settings Preview */}
        {providerSettings?.settings && (
          <div className="mt-4 p-2 bg-muted rounded-lg text-xs space-y-1">
            {providerId === 'local' && providerSettings.settings.path && (
              <p><span className="text-muted-foreground">Path:</span> {providerSettings.settings.path}</p>
            )}
            {providerId === 's3' && (
              <>
                {providerSettings.settings.bucket && <p><span className="text-muted-foreground">Bucket:</span> {providerSettings.settings.bucket}</p>}
                {providerSettings.settings.region && <p><span className="text-muted-foreground">Region:</span> {providerSettings.settings.region}</p>}
              </>
            )}
            {providerId === 'gcs' && (
              <>
                {providerSettings.settings.bucket && <p><span className="text-muted-foreground">Bucket:</span> {providerSettings.settings.bucket}</p>}
                {providerSettings.settings.project && <p><span className="text-muted-foreground">Project:</span> {providerSettings.settings.project}</p>}
              </>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 mt-4">
          <Button
            variant={isActive ? 'default' : 'outline'}
            size="sm"
            className="flex-1"
            onClick={() => onSelect(providerId)}
            disabled={!isConfigured && providerId !== 'local'}
          >
            {isActive ? 'Active' : 'Select'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onTest(providerId)}
            disabled={!isConfigured && providerId !== 'local'}
          >
            <TestTube className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Main System Settings Component
export default function SystemSettings() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [storageSettings, setStorageSettings] = useState(null);
  const [trafficSettings, setTrafficSettings] = useState(null);
  const [testing, setTesting] = useState(null);

  const fetchSettings = useCallback(async () => {
    try {
      const [storageRes, trafficRes] = await Promise.all([
        fetch(`${API_URL}/api/settings/storage`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/settings/traffic`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (storageRes.ok) setStorageSettings(await storageRes.json());
      if (trafficRes.ok) setTrafficSettings(await trafficRes.json());
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSelectStorageProvider = async (providerId) => {
    try {
      const response = await fetch(`${API_URL}/api/settings/storage/provider?provider=${providerId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success(`Storage provider set to ${STORAGE_PROVIDERS[providerId].name}`);
        fetchSettings();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to set provider');
      }
    } catch (error) {
      toast.error('Failed to set provider');
    }
  };

  const handleTestStorageProvider = async (providerId) => {
    setTesting(providerId);
    try {
      const response = await fetch(`${API_URL}/api/settings/storage/test?provider=${providerId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const result = await response.json();

      if (result.success) {
        toast.success(`${STORAGE_PROVIDERS[providerId].name} connection test passed!`);
      } else {
        toast.error(`Test failed: ${result.error}`);
      }
    } catch (error) {
      toast.error('Test failed');
    } finally {
      setTesting(null);
    }
  };

  // Check if user is admin
  if (user?.role !== 'admin') {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            You need administrator privileges to access system settings.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="system-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Settings className="h-7 w-7 text-primary" />
            System Settings
          </h1>
          <p className="text-muted-foreground">
            Configure storage providers, traffic APIs, and other system settings
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchSettings}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <Tabs defaultValue="storage" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="storage">
            <HardDrive className="h-4 w-4 mr-2" />
            Storage
          </TabsTrigger>
          <TabsTrigger value="traffic">
            <Navigation className="h-4 w-4 mr-2" />
            Traffic APIs
          </TabsTrigger>
        </TabsList>

        {/* Storage Tab */}
        <TabsContent value="storage" className="space-y-6">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              Configure where uploaded documents are stored. Select a provider and ensure the required API keys are configured in your environment.
            </AlertDescription>
          </Alert>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(STORAGE_PROVIDERS).map(([id, provider]) => (
                <StorageProviderCard
                  key={id}
                  providerId={id}
                  provider={provider}
                  settings={storageSettings}
                  isActive={storageSettings?.current_provider === id}
                  onSelect={handleSelectStorageProvider}
                  onTest={handleTestStorageProvider}
                />
              ))}
            </div>
          )}

          {/* Configuration Guide */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5" />
                Storage Configuration
              </CardTitle>
              <CardDescription>
                Add these environment variables to your backend/.env file
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 font-mono text-sm">
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-muted-foreground"># Set storage provider (local, s3, gcs)</p>
                  <p>STORAGE_PROVIDER=local</p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-muted-foreground"># Local storage path</p>
                  <p>LOCAL_STORAGE_PATH=/app/uploads/documents</p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-muted-foreground"># AWS S3 Configuration</p>
                  <p>AWS_ACCESS_KEY_ID=your_access_key</p>
                  <p>AWS_SECRET_ACCESS_KEY=your_secret_key</p>
                  <p>S3_BUCKET=your-bucket-name</p>
                  <p>AWS_REGION=us-east-1</p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-muted-foreground"># Google Cloud Storage Configuration</p>
                  <p>GOOGLE_APPLICATION_CREDENTIALS=/path/to/credentials.json</p>
                  <p>GCS_BUCKET=your-bucket-name</p>
                  <p>GCS_PROJECT=your-project-id</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Traffic Tab */}
        <TabsContent value="traffic" className="space-y-6">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              Traffic API settings can be managed from the <strong>Route Optimization</strong> page.
              Navigate there to configure and test traffic providers.
            </AlertDescription>
          </Alert>

          {trafficSettings && (
            <Card>
              <CardHeader>
                <CardTitle>Current Configuration</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground">Active Provider</Label>
                    <p className="font-medium capitalize">{trafficSettings.current_provider}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Configured Providers</Label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {trafficSettings.providers?.filter(p => p.configured).map(p => (
                        <Badge key={p.id} variant="outline" className="text-xs">
                          {p.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
